<template>
  <div>
    <v-row v-for="(row, rowIndex) in tagRows" :key="rowIndex" dense>
      <v-col
        v-for="(tag, colIndex) in row"
        :key="colIndex"
        :class="nodeClasses(tag)"
      >
        <component
          :is="nodeEditors(tag)"
          v-if="tag.multilang == 0"
          v-model="nodeData.fields[tag.id]"
          :tag="tag"
          :dic="dic(tag)"
          :readonly="readonly"
          @title-change="onTitleChange"
        />
        <component
          :is="nodeEditors(tag)"
          v-else
          v-model="nodeData.fields[tag.id][nodeLang]"
          :tag="tag"
          :dic="dic(tag)"
          :readonly="readonly"
          @title-change="onTitleChange"
        />
      </v-col>
    </v-row>
    <v-row
      v-for="(refGroupData, refGroupId) in meta.references"
      :key="refGroupId"
      dense
    >
      <v-col :class="nodeClasses({ typ: 'STRING' })">
        <reference-selector
          v-if="refGroup(refGroupId).multilang == 1"
          :id="`form-autocomplete-refs-${refGroupId}-${nodeLang}`"
          v-model="nodeData.refs[refGroupId][nodeLang]"
          :node-lang="nodeLang"
          :ref-items="refItems(refGroupId)"
          :ref-group-meta="refGroup(refGroupId)"
        />
        <reference-selector
          v-else
          :id="`form-autocomplete-refs-${refGroupId}`"
          v-model="nodeData.refs[refGroupId]"
          :node-lang="nodeLang"
          :ref-items="refItems(refGroupId)"
          :ref-group-meta="refGroup(refGroupId)"
        />
      </v-col>
    </v-row>
    <v-row v-for="mirrorEntry in mirrorsDic" :key="mirrorEntry.parent_node_id">
      <v-col :class="nodeClasses({ typ: 'STRING' })">
        <v-checkbox
          :id="`form-checkbox-mirror-${mirrorEntry.parent_node_id}`"
          v-model="nodeData.mirrors"
          :value="mirrorEntry.parent_node_id"
          inset
          class="pt-0 mt-0 mb-4"
          :readonly="readonly"
          :label="mirrorLabel(mirrorEntry)"
          hide-details
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { computed } from "vue";
import StringEditor from "./Editors/StringEditor.vue";
import DateEditor from "./Editors/DateEditor.vue";
import ScheduleEditor from "./Editors/ScheduleEditor.vue";
import TagsEditor from "./Editors/TagsEditor.vue";
import MemoEditor from "./Editors/MemoEditor.vue";
import ImageEditor from "./Editors/ImageEditor.vue";
import ImageExtEditor from "./Editors/ImageExtEditor.vue";
import ImageGalleryEditor from "./Editors/ImageGalleryEditor.vue";
import ImageMiniatureEditor from "./Editors/ImageMiniatureEditor.vue";
import VideoEditor from "./Editors/VideoEditor.vue";
import FileEditor from "./Editors/FileEditor.vue";
import CodeEditor from "./Editors/CodeEditor.vue";
import TextEditor from "./Editors/TextEditor/TextEditor.vue";
import ReferenceSelector from "./Editors/ReferenceSelector.vue";
import CheckboxEditor from "./Editors/CheckboxEditor.vue";

export default {
  components: {
    ReferenceSelector
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    meta: {
      type: Object,
      required: true
    },
    size: {
      type: String,
      default: "lg"
    },
    nodeLang: {
      type: String,
      required: true
    },
    refsDic: {
      type: Object,
      default: () => {}
    },
    mirrorsDic: {
      type: Array,
      default: () => []
    },
    action: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      required: true
    },
    canSort: {
      type: Boolean,
      required: true
    }
  },

  computed: {
    tagRows() {
      return this.meta.tags.reduce(
        (product, tag) => {
          product[product.length - 1].push(tag);
          if (tag.col_class?.includes("row-breaker") || this.size == "lg") {
            product.push([]);
          }
          return product;
        },
        [[]]
      );
    },
    dic() {
      return tag => {
        return tag.realm_dic_id
          ? this.$store.getters.getDic(tag.realm_dic_id, this.nodeLang)
          : [];
      };
    },
    refGroup() {
      return groupId => this.$store.getters.getMetaLinkGroup(groupId);
    },
    refItems() {
      return groupId => {
        return Object.values(this.refsDic[groupId] || {});
      };
    },
    mirrorLabel() {
      return mirrorEntry => `Отражение в раздел "${mirrorEntry.term}"`;
    },
    nodeEditors() {
      return tag => {
        switch (tag.typ) {
          case "CHECKBOX":
            return CheckboxEditor;
          case "DATE":
            return DateEditor;
          case "SCHEDULE":
            return ScheduleEditor;
          case "MEMO":
            return MemoEditor;
          case "CODE":
            return CodeEditor;
          case "TEXT":
            return TextEditor;
          case "TAGS":
            return TagsEditor;
          case "IMAGE":
            return ImageEditor;
          case "IMAGE_EXT":
            return ImageExtEditor;
          case "IMAGE_GALLERY":
            return ImageGalleryEditor;
          case "IMAGE_MINIATURE":
            return ImageMiniatureEditor;
          case "VIDEO":
            return VideoEditor;
          case "FILE":
            return FileEditor;
          default:
            return StringEditor;
        }
      };
    },
    nodeClasses() {
      return tag => {
        switch (tag.typ) {
          case "SCHEDULE":
            if (this.size == "lg") {
              return "col-12 col-sm-9 offset-sm-2 col-xl-6";
            } else {
              return "col-12";
            }
          case "CODE":
          case "IMAGE_GALLERY":
          case "TEXT":
            if (this.size == "lg") {
              return "col-12 col-sm-9 offset-sm-2";
            } else {
              return "col-12";
            }
          default:
            if (this.size == "lg") {
              return "col-12 col-sm-4 offset-sm-2";
            } else {
              return this.getNameClass;
            }
        }
      };
    },
    getNameClass() {
      switch (this.size) {
        case "xs":
          return "col-12 col-md-8";
        case "sm":
          return "col-12 col-md-6";
        case "md":
          return "col-12 col-md-6";
        case "lg":
          return "col-12 col-md-6 col-lg-4";
      }
      return "";
    }
  },

  methods: {
    onTitleChange(val) {
      this.$emit("title-change", val);
    }
  },

  setup(props, { emit }) {
    const nodeData = computed({
      get: () => props.value,
      set: val => emit("input", val)
    });

    const isCreateAction = computed(() => props.action === "create");
    const isEditAction = computed(() => props.action === "edit");

    return {
      nodeData,
      isCreateAction,
      isEditAction
    };
  }
};
</script>
